'use client';
import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useEffect, useMemo, useState } from 'react';

import { selectProductPrice } from '@/entities/product/lib/selectProductPrice';
import { FavoriteToggle } from '@/features/favorites';
import { AddToCart } from '@/features/product';
import { IMAGE_QUALITY } from '@/shared/config';
import { formatPrice, getProductUrl } from '@/shared/lib';
import { Dropdown, Sticker } from '@/shared/ui';

import styles from './card.module.scss';
import { CardProps } from './config';

export const Card: FC<CardProps> = (props) => {
  const {
    product,
    className,
    isOpenDropDownUp = false,
    onNavigate,
    preferAvailablePrice = false,
  } = props;
  const [selectedOption, setSelectedOption] = useState<string | number | null>(
    () => selectProductPrice(product.prices, null, preferAvailablePrice)?.id ?? null,
  );
  const priceOptions = useMemo(() => {
    return (
      product.prices
        ?.filter((price) => price.price != null && Number.isFinite(price.price))
        .map((price) => {
          return {
            value: price.id,
            label: price.name,
          };
        }) || []
    );
  }, [product]);

  const selectedPrice = useMemo(() => {
    return selectProductPrice(product.prices, selectedOption, preferAvailablePrice);
  }, [product.prices, selectedOption, preferAvailablePrice]);

  const isUniqueProduct =
    priceOptions[0]?.label?.includes('уникальный предмет');

  useEffect(() => {
    setSelectedOption(
      (selected) =>
        selectProductPrice(product.prices, selected, preferAvailablePrice)?.id ?? null,
    );
  }, [product.prices, preferAvailablePrice]);

  return (
    <article
      className={classNames(
        styles.card,
        !selectedPrice?.is_available && styles.card__disabled,
        className,
      )}
    >
      <Link
        className={styles.card__link}
        href={getProductUrl(product)}
        onClick={onNavigate}
      >
        <div className={styles.card__image_block}>
          <FavoriteToggle className={styles.card__like} product={product} />
          <Image
            fill
            alt={`${product.name}`}
            className={styles.card__image}
            quality={IMAGE_QUALITY}
            sizes="(max-width: 1024px) 50vw, 33vw"
            src={product?.image || ''}
          />
          <div className={styles.card__stickerBlock}>
            {product.is_promotion && (
              <Sticker className={styles.card__sticker} variant="white">
                Акция
              </Sticker>
            )}
            {product.is_new && (
              <Sticker className={styles.card__sticker} variant="grey">
                Новинка
              </Sticker>
            )}
            {product.is_hit && (
              <Sticker className={styles.card__sticker} variant="black">
                Хит
              </Sticker>
            )}
          </div>
        </div>
      </Link>
      <div className={styles.card__content}>
        <div className={styles.card__category_block}>
          <p className={styles.card__category}>{product.subcategory_name}</p>
        </div>
        <Link
          className={classNames('link', styles.card__title_block)}
          href={getProductUrl(product)}
          onClick={onNavigate}
        >
          <span>{product.name}</span>
        </Link>
        <div className={styles.card__price_block}>
          <p className={styles.card__price}>
            {formatPrice(
              selectedPrice?.price_with_personal_discount ??
                selectedPrice?.price ??
                0,
            )}
          </p>
          <p className={styles.card__old_price}>
            {formatPrice(selectedPrice?.old_price ?? 0)}
          </p>
        </div>
      </div>
      <div
        className={classNames(
          !isUniqueProduct ? styles.card__bottom : styles.card__bottom_end,
        )}
      >
        {!isUniqueProduct && (
          <Dropdown
            openUp={isOpenDropDownUp}
            options={priceOptions}
            value={selectedPrice?.id ?? null}
            onChange={setSelectedOption}
          />
        )}
        {selectedPrice?.is_available ? (
          <AddToCart product={product} selectedPrice={selectedPrice.id} />
        ) : selectedPrice ? (
          <p className={styles.card__out_of_stock}>Нет в наличии</p>
        ) : null}
      </div>
    </article>
  );
};
